import { useCallback, useMemo, useState } from "react";
import { VStack } from "@design-system/VStack/VStack";
import { SplitButton } from "@design-system/Button/SplitButton";
import { Menu } from "@design-system/Menu/Menu.lib";
import { noop } from "@common/helpers/function";
import { useLineItemsBulkCommandActions } from "@components/lineItems/useLineItemsBulkCommandActions";
import { EagerLoaded } from "@common/types/base/orm";
import { Bill, BillViewOptionGroup } from "@common/types/bill";
import { BillLineItemColumn } from "@common/types/views/billViews";
import { FinancialAccount } from "@common/types/financialAccount";
import { isBillProviderAccount } from "@helpers/bills";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { ViewToolbar } from "@components/views/ViewToolbar/ViewToolbar";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { useSubNavigationPortalContent } from "@hooks/useSubNavigationPortal";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { BillStats } from "@components/bills/BillStats";
import { BillsByVendorViewContainer } from "@components/bills/views/billsByVendorView/BillsByVendorViewContainer";
import { BillLineItemsViewContainer } from "@components/bills/views/billLineItemsView/BillLineItemsViewContainer";
import { CreateRampConnection } from "@components/ramp/CreateRampConnection";
import { CreateBillcomConnection } from "@components/billcom/CreateBillcomConnection";
import { ForwardViaEmailModal } from "@components/accrualDocumentImporter/ForwardViaEmailModal";
import { DetailsAside } from "@design-system/DetailsAside/DetailsAside";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { TablePageHeader } from "../pageHeader/TablePageHeader";
import { FilterTabs } from "../pageHeader/filters/lib";
import { AccountSelectModal } from "../invoices/AccountSelectModal";
import { PaginatedBillsTableContextProvider } from "./PaginatedBillsTableContextProvider";
import styles from "./BillPageContent.module.scss";
import { BillsByCounterpartyTableContextProvider } from "./BillsByCounterpartyTableContextProvider";
import { BillsViewContainer } from "./views/billsView/BillsViewContainer";
import { loadBillAfterRefresh } from "@api/bill.api";
import { useScope } from "@state/scopeContext.lib";
import { BillDetails } from "./BillDetails";
import { createBillsViewConfig } from "./views/billsView/billsViewConfig";
import { billsByVendorViewConfig } from "./views/billsByVendorView/billsByVendorViewConfig";
import { ManualBillEditDrawer } from "./ManualBillEditDrawer/ManualBillEditDrawer";
import { BillAutoSelector } from "./BillAutoSelector";
import { BillsExport } from "./BillsExport";
import { useBillPage } from "./useBillPage";
import { useBillsBulkCreateTask } from "./useBillsBulkCreateTask";
import { useBillScheduleWiring } from "./schedules/useBillScheduleWiring";

const GROUP_BY_LABELS: Record<BillViewOptionGroup, string> = {
    [BillViewOptionGroup.BILL]: "By bill",
    [BillViewOptionGroup.COUNTERPARTY]: "By vendor",
    [BillViewOptionGroup.LINE_ITEMS]: "By line item",
};

interface Props {
    billAccounts: FinancialAccount[];
}

export const BillPageContent: React.FC<Props> = ({ billAccounts }) => {
    const {
        selectedBill,
        setSelectedBill,
        billTableView,
        billLineItemsTableView,
        billsByVendorTableView,
        isManualAccrualCreationEnabled,
        isBillSchedulesEnabled,
        openOverlay,
        hasVisibleAccrualLedgerColumn,
        isClassesEnabled,
        billLineItemsViewConfig,
        bulkUpdateBillLineItems,
        handleUpdateBillMemo,
        handleUpdateBillStatus,
        viewOptions,
        builtInBillViews,
        builtInBillLineItemViews,
        builtInBillsByVendorViews,
        filters,
        setFilters,
        viewOption,
        billStats,
        initialIds,
        selectedBillLineItems,
        hasSelectedByCurrentFilters,
        selectedBillLineItemsCount,
        clearLineItemsSelection,
        handleSelectedLineItemsChange,
        selectAllByCurrentFilters,
        handleBulkClassesSelected,
        handleBulkAccrualAccountSelected,
        handleBulkAccountsPayableSelected,
        bulkAccrualAccountEntityId,
        billRowSelection,
        setBillRowSelection,
        editBills,
        setEditBills,
        clearBillSelection,
        onBillClick,
        handleFiltersChange,
        handleDateRangeChange,
        defaultFilters,
        dateRange,
        handleViewOptionChange,
        handleLineItemsEditClick,
        isLineItemsEditAvailable,
        handleBillViewChange,
        handleBillViewSelect,
        handleBillLineItemsViewChange,
        handleBillLineItemsViewSelect,
        handleBillsByVendorViewChange,
        handleBillsByVendorViewSelect,
        handleClearFilters,
        onCreateBill,
        onCreateBillcomBill,
        createBillAccounts,
        showAccountSelectModal,
        setShowAccountSelectModal,
        onAccountSelect,
        rampConnectionRef,
        billcomConnectionRef,
        hasRampAccounts,
        hasBillcomAccounts,
    } = useBillPage(billAccounts);

    // ACCRUAL_LEDGER also gates the accrual-basis bulk line-item actions
    // below ("Accrual ledger", "Payables account") — same plan feature as
    // the importer entry points, so we share the single hook call.
    const { hasAccess: hasAccrualLedgerAccess } = useAllowFeature(
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
    );
    const hasImporterAccess = hasAccrualLedgerAccess;
    const { hasAccess: hasClassesAccess } = useAllowFeature(
        PREMIUM_FEATURES.CLASSES,
    );
    const { billInvoiceImporter: isDocumentImporterEnabled } = useTypedFlags();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const { workspaceId } = useScope();
    const selectedBillId = selectedBill?.id;
    const refreshSelectedBill = useCallback(() => {
        if (!workspaceId || !selectedBillId) {
            return;
        }
        loadBillAfterRefresh({ workspaceId, billId: selectedBillId })
            .then((refreshedBill) => {
                if (refreshedBill) {
                    setSelectedBill((previous) =>
                        previous?.id === refreshedBill.id
                            ? refreshedBill
                            : previous,
                    );
                }
            })
            .catch(noop);
    }, [workspaceId, selectedBillId, setSelectedBill]);
    const { scheduleWiring, scheduleOverlays } = useBillScheduleWiring(
        isBillSchedulesEnabled,
        { onRefreshed: refreshSelectedBill },
    );

    const viewOptionItems = useMemo<Array<PickerItemData<BillViewOptionGroup>>>(
        () =>
            viewOptions.map((option) => ({
                value: option,
                label: GROUP_BY_LABELS[option],
            })),
        [viewOptions],
    );

    const canImport = isManualAccrualCreationEnabled && hasImporterAccess;
    const canUploadOrForward = canImport && isDocumentImporterEnabled;
    const [isForwardOpen, setIsForwardOpen] = useState(false);

    const onCreateBillOption = (
        value:
            | "provider"
            | "ramp"
            | "billcom"
            | "importer"
            | "documentImporter"
            | "forward",
    ) => {
        // Every branch either leaves the app for the provider or opens a
        // create form the server can only refuse on save. Refuse once, here,
        // rather than in each branch.
        if (refuseIfViewOnly()) {
            return;
        }
        if (value === "provider") {
            if (createBillAccounts.length === 0) {
                rampConnectionRef.current?.initializeConnection();
            } else {
                onCreateBill();
            }
        } else if (value === "ramp") {
            if (!hasRampAccounts) {
                rampConnectionRef.current?.initializeConnection();
            } else {
                onCreateBill();
            }
        } else if (value === "billcom") {
            onCreateBillcomBill();
        } else if (value === "importer") {
            openOverlay("manualBillCreation", {});
        } else if (value === "documentImporter") {
            openOverlay("accrualDocumentImporter", { mode: "bills" });
        } else if (value === "forward") {
            setIsForwardOpen(true);
        }
    };

    const hasConnectedBillAccounts = useMemo(
        () => billAccounts.some(isBillProviderAccount),
        [billAccounts],
    );

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_BILL_PAY_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.invoicing,
    });

    const { createTaskFromBills, createTaskFromFilters } =
        useBillsBulkCreateTask({ view: billTableView });
    const handleLineItemsCreateTask = () => {
        if (hasSelectedByCurrentFilters) {
            createTaskFromFilters(filters);
            return;
        }
        const bills = new Map<string, Bill>();
        for (const lineItem of Object.values(selectedBillLineItems)) {
            if (lineItem.bill) {
                bills.set(lineItem.bill.id, lineItem.bill);
            }
        }
        createTaskFromBills([...bills.values()]);
    };

    const lineItemsBulkActions = useLineItemsBulkCommandActions({
        selectedCount: selectedBillLineItemsCount,
        hasSelectedByCurrentFilters,
        isUpdating: bulkUpdateBillLineItems.isPending,
        hasReceivablesAccount: false,
        hasPayablesAccount: !!bulkAccrualAccountEntityId,
        hasClassesAccess,
        hasAccrualLedgerAccess,
        accrualAccountEntityId: bulkAccrualAccountEntityId,
        onClassesSelected: handleBulkClassesSelected,
        onAccrualAccountSelected: handleBulkAccrualAccountSelected,
        onAccountsPayableSelected: handleBulkAccountsPayableSelected,
        onEditClick: isLineItemsEditAvailable
            ? handleLineItemsEditClick
            : undefined,
        onCreateTaskClick: handleLineItemsCreateTask,
    });

    const billViewConfig = useMemo(
        () =>
            createBillsViewConfig({
                hasAnyAccrualLedgers: hasVisibleAccrualLedgerColumn,
                isSchedulesEnabled: isBillSchedulesEnabled,
            }),
        [hasVisibleAccrualLedgerColumn, isBillSchedulesEnabled],
    );

    const exportColumns = useMemo(() => {
        if (viewOption.groupBy === BillViewOptionGroup.LINE_ITEMS) {
            return billLineItemsTableView.columns
                .map((column) => column.id)
                .filter(
                    (columnId) =>
                        isClassesEnabled ||
                        columnId !== BillLineItemColumn.CLASS,
                );
        }
        if (viewOption.groupBy === BillViewOptionGroup.COUNTERPARTY) {
            return billsByVendorTableView.columns.map((column) => column.id);
        }
        return billTableView.columns.map((column) => column.id);
    }, [
        viewOption.groupBy,
        billLineItemsTableView,
        isClassesEnabled,
        billsByVendorTableView,
        billTableView,
    ]);

    const viewToolbarContent = useMemo(() => {
        if (viewOption.groupBy === BillViewOptionGroup.BILL) {
            return (
                <ViewToolbar
                    view={billTableView}
                    onViewChange={handleBillViewChange}
                    onViewSelect={handleBillViewSelect}
                    viewConfig={billViewConfig}
                    builtInViews={builtInBillViews}
                    defaultFilters={defaultFilters}
                    baseViewCacheKey={
                        WORKSPACE_USER_CACHE_KEYS.BILLS_PAGE_BILL_BASE_VIEW_ID_CACHE_KEY
                    }
                />
            );
        }
        if (viewOption.groupBy === BillViewOptionGroup.LINE_ITEMS) {
            return (
                <ViewToolbar
                    view={billLineItemsTableView}
                    onViewChange={handleBillLineItemsViewChange}
                    onViewSelect={handleBillLineItemsViewSelect}
                    viewConfig={billLineItemsViewConfig}
                    builtInViews={builtInBillLineItemViews}
                    defaultFilters={defaultFilters}
                    baseViewCacheKey={
                        WORKSPACE_USER_CACHE_KEYS.BILLS_PAGE_LINE_ITEMS_BASE_VIEW_ID_CACHE_KEY
                    }
                />
            );
        }
        if (viewOption.groupBy === BillViewOptionGroup.COUNTERPARTY) {
            return (
                <ViewToolbar
                    view={billsByVendorTableView}
                    onViewChange={handleBillsByVendorViewChange}
                    onViewSelect={handleBillsByVendorViewSelect}
                    viewConfig={billsByVendorViewConfig}
                    builtInViews={builtInBillsByVendorViews}
                    defaultFilters={defaultFilters}
                    baseViewCacheKey={
                        WORKSPACE_USER_CACHE_KEYS.BILLS_PAGE_BY_VENDOR_BASE_VIEW_ID_CACHE_KEY
                    }
                />
            );
        }
        return null;
    }, [
        viewOption.groupBy,
        billTableView,
        handleBillViewChange,
        handleBillViewSelect,
        billViewConfig,
        builtInBillViews,
        defaultFilters,
        billLineItemsTableView,
        handleBillLineItemsViewChange,
        handleBillLineItemsViewSelect,
        billLineItemsViewConfig,
        builtInBillLineItemViews,
        billsByVendorTableView,
        handleBillsByVendorViewChange,
        handleBillsByVendorViewSelect,
        builtInBillsByVendorViews,
    ]);

    useSubNavigationPortalContent("bills-view-toolbar", viewToolbarContent);

    return (
        <>
            <TablePageHeader
                search={filters?.search}
                onSearchChange={(value) => {
                    handleFiltersChange({ search: value ?? "" });
                }}
                filters={filters ?? {}}
                onFiltersChange={handleFiltersChange}
                defaultFilters={defaultFilters}
                hideClearButton
                availableFilters={[
                    FilterTabs.EXTERNAL_BILL_STATUSES,
                    ...(hasVisibleAccrualLedgerColumn
                        ? [FilterTabs.BILL_STATUSES]
                        : []),
                    FilterTabs.DUE,
                    FilterTabs.AMOUNT,
                    FilterTabs.ACCOUNT,
                    FilterTabs.MATCH_STATUS,
                    FilterTabs.COUNTERPARTY,
                ]}
                accountFilterPredicate={isBillProviderAccount}
                counterpartyEntityLabel="Vendor"
                isFullPage
                borderBottom
            >
                <div className={styles.buttonGroup}>
                    <DateRangeFilterNext
                        buttonClass={styles.dateRangeFilter}
                        dateRange={dateRange}
                        onChange={handleDateRangeChange}
                        pinnedPresets={pinnedPresets}
                        onTogglePin={onTogglePin}
                    />
                    <Picker.Select
                        items={viewOptionItems}
                        value={viewOption.groupBy}
                        onValueChange={(value) =>
                            handleViewOptionChange(
                                value ?? BillViewOptionGroup.BILL,
                            )
                        }
                        trigger={
                            <Picker.Button
                                variant="primary"
                                size="md"
                                className={styles.viewOptionsPopover}
                            />
                        }
                    />
                </div>
                <EntityFilterCombobox
                    filters={filters ?? {}}
                    onChange={handleFiltersChange}
                    onlyBusiness
                    excludeAllMockEntities
                />
                <BillsExport
                    filters={filters}
                    groupBy={viewOption.groupBy}
                    columns={exportColumns}
                />
                <SplitButton
                    onPrimaryClick={() => onCreateBillOption("provider")}
                    menuAriaLabel="More bill options"
                    menu={
                        <>
                            <Menu.Item
                                onClick={() => onCreateBillOption("ramp")}
                            >
                                Ramp
                            </Menu.Item>
                            <Menu.Item
                                onClick={() => onCreateBillOption("billcom")}
                            >
                                BILL
                            </Menu.Item>
                            {canImport && (
                                <Menu.Item
                                    onClick={() =>
                                        onCreateBillOption("importer")
                                    }
                                >
                                    Importer
                                </Menu.Item>
                            )}
                            {canUploadOrForward && (
                                <Menu.Item
                                    onClick={() =>
                                        onCreateBillOption("documentImporter")
                                    }
                                >
                                    Upload bill
                                </Menu.Item>
                            )}
                            {canUploadOrForward && (
                                <Menu.Item
                                    onClick={() =>
                                        onCreateBillOption("forward")
                                    }
                                >
                                    Forward via email
                                </Menu.Item>
                            )}
                        </>
                    }
                >
                    Create bill
                </SplitButton>
            </TablePageHeader>

            <VStack gap={16} className="p-0">
                <BillStats
                    billStats={billStats}
                    filters={filters}
                    setFilters={setFilters}
                />
                <div className={styles.viewContainer}>
                    {viewOption.groupBy === BillViewOptionGroup.BILL && (
                        <PaginatedBillsTableContextProvider>
                            {initialIds?.length && (
                                <BillAutoSelector
                                    initialIds={initialIds}
                                    onSelect={setSelectedBill}
                                />
                            )}
                            <BillsViewContainer
                                view={billTableView}
                                hasConnectedBillAccounts={
                                    hasConnectedBillAccounts
                                }
                                onClearFilters={handleClearFilters}
                                hasAnyAccrualLedgers={
                                    hasVisibleAccrualLedgerColumn
                                }
                                onBillClick={onBillClick}
                                selectedBillId={selectedBill?.id}
                                rowSelection={billRowSelection}
                                onRowSelectionChange={setBillRowSelection}
                                onClearSelection={clearBillSelection}
                                onEditBills={
                                    isManualAccrualCreationEnabled
                                        ? setEditBills
                                        : undefined
                                }
                                scheduleWiring={scheduleWiring}
                            />
                        </PaginatedBillsTableContextProvider>
                    )}
                    {viewOption.groupBy === BillViewOptionGroup.LINE_ITEMS && (
                        <PaginatedBillsTableContextProvider>
                            {initialIds?.length && (
                                <BillAutoSelector
                                    initialIds={initialIds}
                                    onSelect={setSelectedBill}
                                />
                            )}
                            <BillLineItemsViewContainer
                                view={billLineItemsTableView}
                                hasConnectedBillAccounts={
                                    hasConnectedBillAccounts
                                }
                                onClearFilters={handleClearFilters}
                                hasAnyAccrualLedgers={
                                    hasVisibleAccrualLedgerColumn
                                }
                                onBillClick={onBillClick}
                                selectedBillId={selectedBill?.id}
                                selectedLineItems={selectedBillLineItems}
                                hasSelectedByCurrentFilters={
                                    hasSelectedByCurrentFilters
                                }
                                onSelectedLineItemsChange={
                                    handleSelectedLineItemsChange
                                }
                                selectedCount={selectedBillLineItemsCount}
                                hasSelection={lineItemsBulkActions.hasSelection}
                                onClearSelection={clearLineItemsSelection}
                                onSelectAllByFilters={selectAllByCurrentFilters}
                                onOpenBulkActions={
                                    lineItemsBulkActions.handleOpenBulkActions
                                }
                                scheduleWiring={scheduleWiring}
                            />
                        </PaginatedBillsTableContextProvider>
                    )}
                    {viewOption.groupBy ===
                        BillViewOptionGroup.COUNTERPARTY && (
                        <BillsByCounterpartyTableContextProvider>
                            <BillsByVendorViewContainer
                                view={billsByVendorTableView}
                                hasConnectedBillAccounts={
                                    hasConnectedBillAccounts
                                }
                                onClearFilters={handleClearFilters}
                            />
                        </BillsByCounterpartyTableContextProvider>
                    )}
                    {selectedBill && (
                        <DetailsAside>
                            <BillDetails
                                bill={
                                    selectedBill as EagerLoaded<Bill, "lines">
                                }
                                setBill={setSelectedBill}
                                onEdit={() => setEditBills([selectedBill])}
                                onUpdateMemo={handleUpdateBillMemo}
                                onUpdateStatus={handleUpdateBillStatus}
                                scheduleCallbacks={scheduleWiring?.callbacks}
                            />
                        </DetailsAside>
                    )}
                </div>
            </VStack>
            <AccountSelectModal
                show={showAccountSelectModal}
                onHide={() => setShowAccountSelectModal(false)}
                invoicingAccounts={createBillAccounts}
                onSelect={onAccountSelect}
            />
            {!hasRampAccounts && (
                <CreateRampConnection ref={rampConnectionRef} onCreated={noop}>
                    {() => null}
                </CreateRampConnection>
            )}
            {!hasBillcomAccounts && (
                <CreateBillcomConnection
                    ref={billcomConnectionRef}
                    onCreated={noop}
                >
                    {() => null}
                </CreateBillcomConnection>
            )}
            {isForwardOpen && (
                <ForwardViaEmailModal
                    show
                    mode="bills"
                    onClose={() => setIsForwardOpen(false)}
                />
            )}
            {scheduleOverlays}
            {editBills && (
                <ManualBillEditDrawer
                    bills={editBills}
                    open
                    onClose={() => {
                        setEditBills(null);
                        setSelectedBill(undefined);
                        clearBillSelection();
                        clearLineItemsSelection();
                    }}
                />
            )}
        </>
    );
};
